/**
 * @fileoverview Compose personal profile, phone/PIN/greeting settings and authorized administration sections. Each focused settings surface owns its API contract; this route owns selection and personal draft state. `kc-internal-frontend/src/AuthContext.js` supplies identity and `kc-internal-frontend/src/AppAccessContext.js` supplies workspace context. Profile/phone changes and broader operational configuration remain distinct accepted mutations.
 */

import React, { useContext, useEffect, useRef, useState } from 'react'

import ToastContext from '../ToastContext'
import { useAppAccess } from '../AppAccessContext'
import { AuthContext } from '../AuthContext'
import api from '../api'
import { AppButton, AppDropdown, AppIcon, AppSpinner, AppTextInput, AppToggle } from './shared'
import ProfileAvatarUploader from './shared/ProfileAvatarUploader'
import { AppPageHeader } from './shared'
import { AppTab, AppTabView } from './shared/AppTabView'
import useUrlTabState from './shared/useUrlTabState'
import IntakeAdmin from './Admin/IntakeAdmin'
import NotificationRules from './Admin/NotificationRules'
import CaseAttributeTypes from './Admin/CaseAttributeTypes'
import PortalNotificationSettings from './Admin/PortalNotificationSettings'
import './Settings.css'

const WORK_DAYS = [
    ['Monday', 'M'],
    ['Tuesday', 'T'],
    ['Wednesday', 'W'],
    ['Thursday', 'T'],
    ['Friday', 'F'],
    ['Saturday', 'S'],
    ['Sunday', 'S'],
]

const TIMEZONES = [
    { label: 'Eastern Time', value: 'America/New_York' },
    { label: 'Central Time', value: 'America/Chicago' },
    { label: 'Mountain Time', value: 'America/Denver' },
    { label: 'Pacific Time', value: 'America/Los_Angeles' },
]

const EMPTY_PHONE_FORM = {
    work_hours_start: '08:00',
    work_hours_end: '17:00',
    work_days: WORK_DAYS.slice(0, 5).map(([day]) => day),
    dnd_enabled: false,
    timezone: 'America/New_York',
}



function PersonalSettings() {
    const showToast = useContext(ToastContext)
    const [user, setUser] = useState(null)
    const [phoneProfile, setPhoneProfile] = useState(null)
    const [phoneForm, setPhoneForm] = useState(EMPTY_PHONE_FORM)
    const [loading, setLoading] = useState(true)
    const [savingPhone, setSavingPhone] = useState(false)
    const [pinError, setPinError] = useState(null)

    useEffect(() => {
        let active = true

        Promise.all([
            api.get('/user'),
            api.get('/profile/phone-settings'),
        ])
            .then(

            ([userResponse, phoneResponse]) => {
                if (!active) return
                setUser(userResponse.data)
                setPhoneProfile(phoneResponse.data)
                if (phoneResponse.data?.settings) {
                    setPhoneForm(phoneResponse.data.settings)
                }
            })
            .catch((error) => {
                console.error('Error fetching settings:', error)
                showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load your settings' })
            })
            .finally(() => {
                if (active) setLoading(false)
            })

        return () => {
            active = false
        }
    }, [showToast])

    const toggleWorkDay =

    (day) => {
        setPhoneForm((current) => ({
            ...current,
            work_days: current.work_days.includes(day)
                ? current.work_days.filter((value) => value !== day)
                : WORK_DAYS.map(([value]) => value).filter(
                    (value) => value === day || current.work_days.includes(value)
                ),
        }))
    }

    const savePhoneSettings =

    async () => {
        const payload = { ...phoneForm }
        for (const [key, pattern, message] of [
            ['voicemail_pin', /^[0-9]{4,8}$/, 'Voicemail PIN must be 4–8 digits.'],
            ['conference_pin', /^[0-9]{8}$/, 'Conference PIN must be exactly 8 digits.'],
        ]) {
            if ((phoneForm[key] ?? '') === (phoneProfile?.settings?.[key] ?? '') ||
                (key === 'conference_pin' && !phoneProfile?.conference_pin_eligible)) {
                delete payload[key]
            } else if (!pattern.test(phoneForm[key] ?? '')) {
                setPinError({ field: key, message })
                return
            }
        }
        setPinError(null)
        setSavingPhone(true)
        try {
            const response = await api.put('/profile/phone-settings', payload)
            setPhoneProfile(response.data)
            setPhoneForm(response.data.settings)
            showToast({ severity: 'success', summary: 'Saved', detail: 'Phone settings updated' })
        } catch (error) {
            // Axios errors include request bodies, which may contain PINs.
            if (error?.response?.data?.field) {
                setPinError({ field: error.response.data.field, message: error.response.data.error })
            }
            showToast({
                severity: 'error',
                summary: 'Could not save',
                detail: error?.response?.data?.error || 'Phone settings could not be updated',
            })
        } finally {
            setSavingPhone(false)
        }
    }

    if (loading) {
        return (
            <div className="settings-page settings-page--loading">
                <AppSpinner />
            </div>
        )
    }

    return (
        <div className="settings-page">
            <div className="settings-grid">
                <section className="settings-card settings-profile-card" aria-labelledby="profile-heading">
                    <div className="settings-card-heading">
                        <span className="settings-card-icon" aria-hidden="true">
                            <AppIcon icon="pi pi-user" />
                        </span>
                        <div>
                            <h2 id="profile-heading">Profile</h2>
                            <p>Your identity across the app</p>
                        </div>
                    </div>

                    <div className="settings-profile">
                        <ProfileAvatarUploader
                            avatarUrl={user?.ProfilePic}
                            name={user?.Name}
                            onAvatarChange={(url) => setUser((previous) => ({ ...previous, ProfilePic: url }))}
                            buttonLabel="Change Photo"
                            size={88}
                        />
                        <div>
                            <div className="settings-profile-name">{user?.Name}</div>
                            <div className="settings-muted">{user?.Email}</div>
                        </div>
                    </div>
                </section>

                <section className="settings-card settings-phone-card" aria-labelledby="phone-heading">
                    <div className="settings-card-heading settings-card-heading--phone">
                        <span className="settings-card-icon" aria-hidden="true">
                            <AppIcon icon="pi pi-phone" />
                        </span>
                        <div>
                            <h2 id="phone-heading">Phone</h2>
                            <p>Control when calls can reach you</p>
                        </div>
                        {phoneProfile?.attorney?.extension_number && (
                            <span className="settings-extension">
                                Ext. {phoneProfile.attorney.extension_number}
                            </span>
                        )}
                    </div>

                    {!phoneProfile?.eligible ? (
                        <div className="settings-phone-unavailable" role="status">
                            <AppIcon icon="pi pi-info-circle" />
                            <span>{phoneProfile?.message || 'Phone settings are not available for this profile.'}</span>
                        </div>
                    ) : (
                        <>
                            <div className={`settings-dnd${phoneForm.dnd_enabled ? ' settings-dnd--active' : ''}`}>
                                <span className="settings-dnd-icon" aria-hidden="true">
                                    <AppIcon icon={phoneForm.dnd_enabled ? 'pi pi-ban' : 'pi pi-phone'} />
                                </span>
                                <label htmlFor="phone-dnd">
                                    <strong>Do not disturb</strong>
                                    <span>{phoneForm.dnd_enabled ? 'Calls go straight to your unavailable route' : 'Calls follow your working hours'}</span>
                                </label>
                                <AppToggle
                                    id="phone-dnd"
                                    checked={phoneForm.dnd_enabled}
                                    onChange={({ value }) => setPhoneForm((current) => ({ ...current, dnd_enabled: value }))}
                                    ariaLabel="Do not disturb"
                                />
                            </div>

                            <div className="settings-phone-fields">
                                <fieldset className="settings-field settings-work-days">
                                    <legend>Working days</legend>
                                    <div className="settings-day-picker">
                                        {WORK_DAYS.map(([day, shortLabel]) => {
                                            const selected = phoneForm.work_days.includes(day)
                                            return (
                                                <button
                                                    key={day}
                                                    type="button"
                                                    className={selected ? 'is-selected' : ''}
                                                    aria-pressed={selected}
                                                    aria-label={day}
                                                    onClick={() => toggleWorkDay(day)}
                                                >
                                                    {shortLabel}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </fieldset>

                                <div className="settings-time-row">
                                    <label className="settings-field" htmlFor="work-hours-start">
                                        <span>Start</span>
                                        <AppTextInput
                                            id="work-hours-start"
                                            type="time"
                                            value={phoneForm.work_hours_start}
                                            onChange={(event) => setPhoneForm((current) => ({
                                                ...current,
                                                work_hours_start: event.target.value,
                                            }))}
                                        />
                                    </label>
                                    <span className="settings-time-arrow" aria-hidden="true">
                                        <AppIcon icon="pi pi-arrow-right" />
                                    </span>
                                    <label className="settings-field" htmlFor="work-hours-end">
                                        <span>End</span>
                                        <AppTextInput
                                            id="work-hours-end"
                                            type="time"
                                            value={phoneForm.work_hours_end}
                                            onChange={(event) => setPhoneForm((current) => ({
                                                ...current,
                                                work_hours_end: event.target.value,
                                            }))}
                                        />
                                    </label>
                                </div>

                                <div className="settings-field">
                                    <label htmlFor="phone-timezone">Time zone</label>
                                    <AppDropdown
                                        id="phone-timezone"
                                        value={phoneForm.timezone}
                                        options={TIMEZONES}
                                        onChange={({ value }) => setPhoneForm((current) => ({ ...current, timezone: value }))}
                                    />
                                </div>
                            </div>

                            <div className="settings-phone-pins">
                                {[
                                    ['voicemail_pin', 'Voicemail PIN', '4–8 digits'],
                                    ...(phoneProfile?.conference_pin_eligible
                                        ? [['conference_pin', 'Conference line PIN', '8 digits']]
                                        : []),
                                ].map(([key, label, hint]) => (
                                    <PhonePinInput
                                        key={key}
                                        id={key}
                                        label={label}
                                        hint={hint}
                                        value={phoneForm[key] ?? ''}
                                        disabled={savingPhone}
                                        error={pinError?.field === key ? pinError.message : null}
                                        onChange={(value) => {
                                            setPhoneForm((current) => ({ ...current, [key]: value }))
                                            setPinError(null)
                                        }}
                                    />
                                ))}
                            </div>

                            <PhoneRecordingRecorder
                                kind="name"
                                greeting={phoneProfile?.name_recording}
                                onProfileChange={setPhoneProfile}
                                showToast={showToast}
                            />
                            <PhoneRecordingRecorder
                                greeting={phoneProfile?.voicemail_greeting}
                                onProfileChange={setPhoneProfile}
                                showToast={showToast}
                            />

                            <div className="settings-card-actions">
                                <AppButton
                                    icon="pi pi-save"
                                    label={savingPhone ? 'Saving' : 'Save phone settings'}
                                    disabled={savingPhone || phoneForm.work_days.length === 0}
                                    onClick={savePhoneSettings}
                                />
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    )
}



function PhonePinInput({ id, label, hint, value, disabled, error, onChange }) {
    const [visible, setVisible] = useState(false)
    return (
        <div className="settings-field">
            <label htmlFor={id}>{label}</label>
            <div className="settings-pin-input">
                <AppTextInput
                    id={id}
                    type={visible ? 'text' : 'password'}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={8}
                    placeholder={hint}
                    value={value}
                    disabled={disabled}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? `${id}-error` : undefined}
                    onChange={(event) => onChange(event.target.value)}
                />
                <button
                    type="button"
                    aria-label={`${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
                    aria-pressed={visible}
                    onClick={() => setVisible((current) => !current)}
                >
                    <AppIcon icon={visible ? 'pi pi-eye-slash' : 'pi pi-eye'} />
                </button>
            </div>
            {error && <div id={`${id}-error`} className="settings-pin-error" role="alert">{error}</div>}
        </div>
    )
}

function PhoneRecordingRecorder({ kind = 'voicemail', greeting, onProfileChange, showToast }) {
    const isName = kind === 'name'
    const endpoint = `/profile/phone-settings/${isName ? 'name-recording' : 'voicemail-greeting'}`
    const title = isName ? 'Name recording' : 'Voicemail greeting'
    const maxSeconds = isName ? 30 : 120
    const fileRef = useRef(null)
    const recorderRef = useRef(null)
    const streamRef = useRef(null)
    const chunksRef = useRef([])
    const timerRef = useRef(null)
    const [recording, setRecording] = useState(false)
    const [seconds, setSeconds] = useState(0)
    const [preview, setPreview] = useState(null)
    const [saving, setSaving] = useState(false)

    useEffect(() => () => {
        if (timerRef.current) clearInterval(timerRef.current)
        if (preview?.url) URL.revokeObjectURL(preview.url)
        streamRef.current?.getTracks().forEach((track) => track.stop())
    }, [preview])

    const finishRecording =

    () => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
    }

    const startRecording =

    async () => {
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            showToast({ severity: 'error', summary: 'Microphone unavailable', detail: 'This browser cannot record audio.' })
            return
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            streamRef.current = stream
            chunksRef.current = []
            const preferredType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm']
                .find((type) => MediaRecorder.isTypeSupported?.(type))
            const recorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined)
            recorderRef.current = recorder
            recorder.ondataavailable = (event) => {
                if (event.data?.size) chunksRef.current.push(event.data)
            }
            recorder.onstop =

            () => {
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
                if (preview?.url) URL.revokeObjectURL(preview.url)
                setPreview({ blob, url: URL.createObjectURL(blob) })
                setRecording(false)
                stream.getTracks().forEach((track) => track.stop())
                streamRef.current = null
                if (timerRef.current) clearInterval(timerRef.current)
            }
            recorder.start(250)
            setSeconds(0)
            setRecording(true)
            timerRef.current = setInterval(() => {
                setSeconds((current) => {
                    if (current >= maxSeconds - 1) finishRecording()
                    return Math.min(current + 1, maxSeconds)
                })
            }, 1000)
        } catch (error) {
            streamRef.current?.getTracks().forEach((track) => track.stop())
            streamRef.current = null
            console.error(error)
            showToast({ severity: 'error', summary: 'Microphone unavailable', detail: 'Allow microphone access to record audio.' })
        }
    }

    const saveGreeting =

    async () => {
        if (!preview?.blob) return
        setSaving(true)
        const formData = new FormData()
        const extension = preview.blob.type.includes('mp4') ? 'm4a' : 'webm'
        formData.append('audio', preview.blob, preview.filename || `${kind}.${extension}`)
        try {
            const response = await api.post(endpoint, formData)
            onProfileChange(response.data)
            URL.revokeObjectURL(preview.url)
            setPreview(null)
            showToast({ severity: 'success', summary: `${title} saved`, detail: 'Your recording is queued for the phone system' })
        } catch (error) {
            showToast({ severity: 'error', summary: 'Could not save', detail: error?.response?.data?.error || 'The recording could not be uploaded' })
        } finally {
            setSaving(false)
        }
    }

    const restoreDefault = async () => {
        if (!window.confirm(isName ? 'Remove your name recording and use the phone system’s default name announcement?' : 'Restore the generated default voicemail greeting?')) return
        setSaving(true)
        try {
            const response = await api.delete(endpoint)
            onProfileChange(response.data)
            showToast({ severity: 'success', summary: 'Default restored', detail: 'The default is queued for the phone system' })
        } catch (error) {
            showToast({ severity: 'error', summary: 'Could not restore', detail: error?.response?.data?.error || 'The recording could not be removed' })
        } finally {
            setSaving(false)
        }
    }

    const savedAudioUrl = greeting?.playback_url
        ? `${String(api.defaults?.baseURL || '/api').replace(/\/$/, '')}${greeting.playback_url}`
        : null
    const stateLabel = greeting?.state === 'synced' ? 'Synced' : greeting?.state === 'error' ? 'Error' : greeting?.state === 'pending' ? 'Pending' : isName ? 'Default name' : 'Generated default'

    return (
        <section className="settings-voicemail" aria-labelledby={`${kind}-heading`}>
            <div className="settings-voicemail__heading">
                <div>
                    <h3 id={`${kind}-heading`}>{title}</h3>
                    <p>{isName ? 'Record or upload just your name for the phone directory. Up to 30 seconds.' : 'Record the message callers hear when you cannot answer.'}</p>
                </div>
                <span className={`settings-voicemail__state is-${greeting?.state || 'default'}`}>
                    <AppIcon icon={greeting?.state === 'synced' ? 'pi pi-check-circle' : greeting?.state === 'error' ? 'pi pi-exclamation-circle' : 'pi pi-clock'} />
                    {stateLabel}
                </span>
            </div>

            {isName && <input
                ref={fileRef}
                type="file"
                accept="audio/*,.wav,.mp3,.m4a,.ogg,.webm"
                aria-label="Upload name recording"
                hidden
                onChange={(event) => {
                    const file = event.target.files?.[0]
                    event.target.value = ''
                    if (!file) return
                    if (file.size > 10 * 1024 * 1024) {
                        showToast({ severity: 'error', summary: 'File too large', detail: 'Choose an audio file that is 10 MB or smaller.' })
                        return
                    }
                    setPreview({ blob: file, filename: file.name, url: URL.createObjectURL(file) })
                }}
            />}
            {recording ? (
                <div className="settings-recorder is-recording" role="status">
                    <span className="settings-recorder__pulse" aria-hidden="true" />
                    <strong>Recording</strong>
                    <time>{formatRecordingTime(seconds)}</time>
                    <AppButton variant="danger" icon="pi pi-stop" label="Stop recording" onClick={finishRecording} />
                </div>
            ) : preview ? (
                <div className="settings-recorder">
                    <audio controls src={preview.url} aria-label={`New ${title.toLowerCase()} preview`} />
                    <div className="settings-recorder__actions">
                        {isName && <AppButton variant="secondary" icon="pi pi-times" label="Cancel" onClick={() => setPreview(null)} disabled={saving} />}
                        <AppButton variant="secondary" icon="pi pi-replay" label="Record again" onClick={startRecording} disabled={saving} />
                        <AppButton icon="pi pi-check" label={saving ? 'Saving' : isName ? 'Use this name recording' : 'Use this greeting'} onClick={saveGreeting} disabled={saving} />
                    </div>
                </div>
            ) : (
                <div className="settings-recorder">
                    {savedAudioUrl ? <audio controls src={savedAudioUrl} aria-label={`Current ${title.toLowerCase()}`} /> : null}
                    <div className="settings-recorder__actions">
                        <AppButton variant="secondary" icon="pi pi-microphone" label={isName ? 'Record name' : 'Record greeting'} onClick={startRecording} disabled={saving} />
                        {isName && <AppButton variant="secondary" icon="pi pi-upload" label="Upload audio" onClick={() => fileRef.current?.click()} disabled={saving} />}
                        {greeting?.configured ? <AppButton variant="secondary" icon="pi pi-undo" label={isName ? 'Remove name recording' : 'Restore default'} onClick={restoreDefault} disabled={saving} /> : null}
                    </div>
                </div>
            )}
        </section>
    )
}



function formatRecordingTime(seconds) {
    const minutes = Math.floor(seconds / 60)
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}



function Settings() {
    const { canAccess } = useAppAccess()
    const { isAdmin } = useContext(AuthContext) || {}
    const tabs = [
        { id: 'personal', label: 'Personal', content: <PersonalSettings /> },
        { id: 'intake', label: 'Intake', content: <IntakeAdmin embedded /> },
        { id: 'notification-rules', label: 'Notification Rules', content: <NotificationRules embedded /> },
        { id: 'client-portal', label: 'Client Portal', content: <PortalNotificationSettings /> },
        { id: 'case-attribute-types', label: 'Case Attribute Types', content: <CaseAttributeTypes embedded /> },
    ].filter(({ id }) => id === 'personal' || (id === 'client-portal' ? isAdmin === true : canAccess(`settings-${id}`)))
    const { activeIndex, onTabChange } = useUrlTabState(tabs.map(({ id }) => id))

    useEffect(() => {
        document.title = 'Settings'
    }, [])

    return (
        <AppTabView
            activeIndex={activeIndex}
            onTabChange={onTabChange}
            variant="route"
            header={
                <AppPageHeader
                    embedded
                    eyebrow="Workspace and personal"
                    title="Settings"
                    icon="pi pi-sliders-h text-xl"
                />
            }
        >
            {tabs.map(({ id, label, content }) => <AppTab key={id} header={label}>{content}</AppTab>)}
        </AppTabView>
    )
}

export default Settings
